import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { resolveModuleFromPath } from '@/config/appNavigation';
import { getModuleEntryByErpModule } from '@/config/moduleEntries';
import type { ErpModule } from '@/api/platform.api';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { setWorkspaceModule } from '@/store/authSlice';
import { getLauncherModules } from '@/utils/moduleAccess';
import {
  canAccessWorkspacePath,
  consumePreSwitchPath,
  firstAccessibleModuleHome,
  PERMISSION_DENIED_EVENT,
  type PermissionDeniedDetail,
} from '@/utils/moduleNavigation';
import { resolveSafeRevertPath } from '@/utils/routeAccess';
import { isOpenAccessRoute } from '@/utils/openAccessRoutes';
import { getPostLoginPath, HR_ESS_HOME_PATH, isHrEssOnlyUser } from '@/utils/moduleScope';
import { MODULE_HUB_PATH } from '@/constants/authRoutes';
import { notify } from '@/core/toast/notify';
import { usePermissions } from '@/hooks/usePermissions';

/** Redirect when a GET API returns 403 — keeps users off pages they cannot access. */
export function usePermissionDeniedRecovery(activeModule: ErpModule | null) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const enabledModules = useAppSelector((s) => s.auth.enabledModules);
  const accessibleModules = useAppSelector((s) => s.auth.accessibleModules);
  const { has, permissions } = usePermissions();
  const roleCode = useAppSelector((s) => s.auth.user?.roleCode);
  const entryModule = useAppSelector((s) => s.auth.entryModule);

  const launcherApps = useMemo(
    () => getLauncherModules(enabledModules, permissions, accessibleModules, roleCode, has),
    [enabledModules, permissions, accessibleModules, roleCode, has],
  );

  const launcherCodes = useMemo(
    () => launcherApps.map((entry) => entry.erpModule),
    [launcherApps],
  );

  const canAccessPath = useMemo(
    () => (pathname: string) =>
      canAccessWorkspacePath(pathname, enabledModules, launcherCodes, accessibleModules),
    [accessibleModules, enabledModules, launcherCodes],
  );

  useEffect(() => {
    const onDenied = (event: Event) => {
      const detail = (event as CustomEvent<PermissionDeniedDetail>).detail;
      if (!detail?.message) return;

      if (isOpenAccessRoute(location.pathname)) {
        return;
      }

      notify.error(detail.message);

      const blockedPath = location.pathname;
      const moduleHome =
        (activeModule && getModuleEntryByErpModule(activeModule)?.homePath) ||
        firstAccessibleModuleHome(launcherCodes) ||
        getPostLoginPath(entryModule ?? null, MODULE_HUB_PATH, {
          permissions,
          accessibleModules,
          roleCode,
        });

      const essFallback =
        isHrEssOnlyUser(permissions, accessibleModules, roleCode) &&
        (blockedPath === '/hr' || blockedPath.startsWith('/hr/'))
          ? HR_ESS_HOME_PATH
          : moduleHome;

      const preSwitch = consumePreSwitchPath();
      const revertTo = resolveSafeRevertPath(blockedPath, essFallback, {
        canAccess: canAccessPath,
        extraCandidates: preSwitch ? [preSwitch] : [],
      });

      if (!revertTo) return;

      const current = `${location.pathname}${location.search}`;
      if (revertTo === current) return;

      const module = resolveModuleFromPath(revertTo.split('?')[0]);
      if (module) {
        dispatch(setWorkspaceModule(module));
      }
      navigate(revertTo, { replace: true });
    };

    window.addEventListener(PERMISSION_DENIED_EVENT, onDenied);
    return () => window.removeEventListener(PERMISSION_DENIED_EVENT, onDenied);
  }, [
    activeModule,
    canAccessPath,
    dispatch,
    entryModule,
    launcherCodes,
    location.pathname,
    location.search,
    navigate,
    permissions,
    accessibleModules,
    roleCode,
  ]);
}
